import { useState } from "react";
import { Link } from "react-router-dom";
import type { Client } from "@/api/schemas";
import { Modal } from "@/components/common/Modal";
import { BARRA_INFERIOR, destinoPorId, gruposDeRol, rutaDeCliente, type DestinoId, type Rol } from "@/routes/routeConfig";
import type { NavBadge } from "@/utils/navBadges";
import { NavIcon } from "./NavIcon";
import styles from "./BottomTabBar.module.css";

interface BottomTabBarProps {
  rol: Rol;
  cliente: Client | null;
  destinoActivo: DestinoId | null;
  badges: Partial<Record<DestinoId, NavBadge>>;
  onOpenBrake: () => void;
}

function Counter({ badge }: { badge: NavBadge | undefined }) {
  if (!badge) return null;
  return (
    <>
      {badge.count ? (
        <span className={styles.count}>
          {badge.count}
          <span className="visually-hidden"> {badge.countText}</span>
        </span>
      ) : null}
      {badge.dot ? <span className={`${styles.dot} ${badge.dot === "red" ? styles.dotRed : styles.dotAmber}`} role="img" aria-label={badge.dotText} /> : null}
    </>
  );
}

/**
 * Barra inferior por debajo de 600 px (visual.md §4). Agencia: Inicio · Leads · Citas · WhatsApp · Más
 * (una hoja con el resto, agrupado). Tienda: sus 5 secciones. Objetivos de 44 px como mínimo y
 * `env(safe-area-inset-bottom)` debajo.
 */
export function BottomTabBar({ rol, cliente, destinoActivo, badges, onOpenBrake }: BottomTabBarProps) {
  const [masAbierto, setMasAbierto] = useState(false);
  const { principales, conMas } = BARRA_INFERIOR[rol];
  if (!cliente) return null;
  const principalesSet = new Set<DestinoId>(principales);
  const activoEnMas = destinoActivo !== null && !principalesSet.has(destinoActivo);

  return (
    <>
      <nav className={styles.bar} aria-label={`Navegación inferior de ${cliente.name}`}>
        {principales.map((id) => {
          const destino = destinoPorId(id);
          const activo = destinoActivo === id;
          return (
            <Link
              key={id}
              to={rutaDeCliente(cliente.slug, destino.ruta)}
              className={`${styles.tab} ${activo ? styles.tabActive : ""}`}
              aria-current={activo ? "page" : undefined}
            >
              <span className={styles.iconWrap}>
                <NavIcon name={id} size={24} />
                <Counter badge={badges[id]} />
              </span>
              <span className={styles.label}>{destino.shortLabel ?? destino.label}</span>
            </Link>
          );
        })}
        {conMas ? (
          <button type="button" className={`${styles.tab} ${activoEnMas ? styles.tabActive : ""}`} aria-haspopup="dialog" aria-expanded={masAbierto} onClick={() => setMasAbierto(true)}>
            <span className={styles.iconWrap}>
              <NavIcon name="mas" size={24} />
              {badges.propuestas?.dot || badges.conexiones?.dot ? <span className={`${styles.dot} ${styles.dotRed}`} role="img" aria-label="Hay avisos en las secciones de «Más»" /> : null}
            </span>
            <span className={styles.label}>Más</span>
          </button>
        ) : null}
      </nav>
      {masAbierto ? (
        <Modal label="Más secciones" placement="bottom" onClose={() => setMasAbierto(false)}>
          <div className={styles.handle} aria-hidden="true" />
          <div className={styles.sheetHead}>
            <h2 className={styles.sheetTitle}>Más secciones</h2>
            <button type="button" className={styles.close} onClick={() => setMasAbierto(false)}>Cerrar</button>
          </div>
          <nav aria-label={`Más secciones de ${cliente.name}`}>
            {gruposDeRol(rol).map((grupo) => {
              const resto = grupo.destinos.filter((d) => !principalesSet.has(d.id));
              if (resto.length === 0) return null;
              return (
                <div key={grupo.titulo ?? "sin-titulo"} className={styles.sheetGroup}>
                  {grupo.titulo ? <p className={styles.sheetGroupTitle}>{grupo.titulo}</p> : null}
                  {resto.map((destino) => (
                    <Link
                      key={destino.id}
                      to={rutaDeCliente(cliente.slug, destino.ruta)}
                      className={`${styles.sheetItem} ${destinoActivo === destino.id ? styles.sheetItemActive : ""}`}
                      aria-current={destinoActivo === destino.id ? "page" : undefined}
                      onClick={() => setMasAbierto(false)}
                    >
                      <NavIcon name={destino.id} />
                      <span className={styles.sheetLabel}>{destino.label}</span>
                      <Counter badge={badges[destino.id]} />
                    </Link>
                  ))}
                </div>
              );
            })}
            <div className={styles.sheetGroup}>
              <p className={styles.sheetGroupTitle}>Agencia</p>
              <Link to="/clientes" className={styles.sheetItem} onClick={() => setMasAbierto(false)}>
                <NavIcon name="clientes" />
                <span className={styles.sheetLabel}>Clientes</span>
              </Link>
              <button type="button" className={styles.sheetItem} onClick={() => { setMasAbierto(false); onOpenBrake(); }}>
                <span className={styles.sheetLabel}>Parar cambios</span>
              </button>
            </div>
          </nav>
        </Modal>
      ) : null}
    </>
  );
}
